import React, { useState } from 'react';
import { Plus, Settings, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Protected groups that cannot be deleted
const PROTECTED_GROUP_IDS = new Set(['mmv-core', 'bap', 'hfs']);
const PROTECTED_GROUP_KINDS = new Set(['original', 'program', 'legacy', 'system']);

const isProtectedGroup = (group) => {
  if (PROTECTED_GROUP_IDS.has(group.id)) return true;
  if (PROTECTED_GROUP_KINDS.has(group.kind)) return true;
  return false;
};

export const StoreGroupList = ({ clientId, storeGroups, onCreateGroup, onManageGroup, onGroupDeleted }) => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const toggleExpand = (groupId) => {
    setExpandedGroup(expandedGroup === groupId ? null : groupId);
  };

  const handleManageClick = (e, group) => {
    e.stopPropagation();
    onManageGroup(group);
  };

  const handleDeleteClick = (e, group) => {
    e.stopPropagation();
    setDeleteConfirm(group);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const response = await fetch(
        `${API_URL}/api/admin/unified-clients/${clientId}/store-groups/${deleteConfirm.id}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete group');
      }

      setDeleteConfirm(null);
      onGroupDeleted();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="store-group-list">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Store Groups ({storeGroups?.length || 0})
        </h3>
        <button
          onClick={onCreateGroup}
          className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          data-testid="create-group-button"
        >
          <Plus className="h-4 w-4 mr-1" />
          Create Group
        </button>
      </div>

      {/* Empty State */}
      {(!storeGroups || storeGroups.length === 0) && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No store groups found. Create one to organize your stores.
        </div>
      )}

      {/* Groups List */}
      {storeGroups && storeGroups.length > 0 && storeGroups.map(group => (
        <div
          key={group.id}
          className="bg-white rounded-lg shadow overflow-hidden"
          data-testid={`store-group-${group.id}`}
        >
          {/* Group Header */}
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleExpand(group.id)}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                  <span>Kind: {group.kind || '—'}</span>
                  <span>·</span>
                  <span>{group.store_ids?.length || 0} stores</span>
                  <span>·</span>
                  <span className={group.is_active ? 'text-green-600' : 'text-gray-400'}>
                    {group.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => handleManageClick(e, group)}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  data-testid={`manage-group-${group.id}`}
                >
                  <Settings className="w-4 h-4 inline mr-1" />
                  Manage
                </button>
                {!isProtectedGroup(group) && (
                  <button
                    onClick={(e) => handleDeleteClick(e, group)}
                    className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
                    data-testid={`delete-group-${group.id}`}
                    title="Delete this group"
                  >
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                )}
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedGroup === group.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {expandedGroup === group.id && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              {/* Metadata */}
              {group.metadata && Object.keys(group.metadata).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Metadata</h4>
                  <div className="bg-white rounded border border-gray-200 p-3">
                    <pre className="text-xs text-gray-600 overflow-x-auto">
                      {JSON.stringify(group.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Store IDs */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Store IDs ({group.store_ids?.length || 0})
                </h4>
                <div className="bg-white rounded border border-gray-200 p-3 max-h-60 overflow-y-auto">
                  {group.store_ids && group.store_ids.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {group.store_ids.map((storeId, index) => (
                        <div
                          key={storeId}
                          className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700 truncate"
                          title={storeId}
                        >
                          {index + 1}. {storeId}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No stores in this group</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6" data-testid="delete-group-confirm-dialog">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Store Group</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the group <strong>"{deleteConfirm.name}"</strong>?
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Deleting this group will NOT delete any stores. 
                Stores will remain in the system and in other groups they belong to.
              </p>
            </div>
            {deleteError && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded mb-4" data-testid="delete-group-error">
                {deleteError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
                data-testid="cancel-delete-group"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-delete-group"
              >
                {deleting ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreGroupList;
